import React, { useCallback, useMemo, useState } from "react";
import { FiChevronDown, FiChevronUp, FiEye, FiEyeOff, FiSliders } from "react-icons/fi";
import {
	ComponentTypes,
	resolveSpanSource,
	resolveVisible,
	type All8PageContent
} from "@bcc/core-renderers";
import {
	createComponentInstance,
	resolveComponentDefinition,
	type ComponentDefinition,
	type Path
} from "../../Pages/pageComponentLibrary";
import { createRuntimeImageComponent, resolveStorageUrl } from "../../Helper/storage";
import { reorderList } from "../LayoutEditor/utils";
import { duplicateComponentAt } from "./componentListUtils";
import { getComponentRuntimeKey } from "./pageEditorUtils";
import PageEditorComponentsPanel from "./PageEditorComponentsPanel";
import PageEditorComponentLibraryModal from "./PageEditorComponentLibraryModal";
import { useComponentLibraryPicker } from "./useComponentLibraryPicker";

type PageEditorLayoutSectionEditorProps = {
	editable: boolean;
	componentIndex: number;
	previewComponents: All8PageContent;
	updateComponentValue: (index: number, path: Path, value: any) => void;
	allowImageVariables?: boolean;
};

type BreakpointKey = "base" | "md" | "lg";

const BREAKPOINTS: { key: BreakpointKey; label: string }[] = [
	{ key: "base", label: "Mobile" },
	{ key: "md", label: "Tablette" },
	{ key: "lg", label: "Bureau" }
];

const BREAKPOINT_LABELS: Record<BreakpointKey, string> = { base: "Mobile", md: "Tablette", lg: "Bureau" };

const SPAN_PRESETS: { value: number; label: string }[] = [
	{ value: 12, label: "1/1" },
	{ value: 9, label: "3/4" },
	{ value: 8, label: "2/3" },
	{ value: 6, label: "1/2" },
	{ value: 4, label: "1/3" },
	{ value: 3, label: "1/4" }
];

const GAP_OPTIONS = [
	{ value: "none", label: "Aucun" },
	{ value: "sm", label: "Petit" },
	{ value: "md", label: "Moyen" },
	{ value: "lg", label: "Grand" }
];

const ALIGN_OPTIONS = [
	{ value: "start", label: "Haut" },
	{ value: "center", label: "Centre" },
	{ value: "stretch", label: "Étiré" }
];

/** French fraction label for a 12-grid span (12 → « 1/1 », 6 → « 1/2 », …). */
function fractionLabel(span: number): string {
	const preset = SPAN_PRESETS.find((p) => p.value === span);
	return preset ? preset.label : `${span}/12`;
}

type LayoutChild = { key?: string; componentType?: string; content?: any; span?: any; visible?: any };

function readChildren(component: any): LayoutChild[] {
	const children = component?.content?.children;
	return Array.isArray(children) ? (children as LayoutChild[]) : [];
}

/**
 * Bespoke editor for the Container (LayoutSection): N child components on a
 * 12-column responsive grid. The children are edited through a NESTED
 * `PageEditorComponentsPanel` : the exact same drag-and-drop canvas as the
 * page root : wrapped in a dotted border so the Container is clearly
 * identifiable. Per-child responsive settings (width per breakpoint + visibility,
 * resolved through the SDK's own `resolveSpanSource` so editor and renderer never
 * diverge) live in a collapsible « Responsive » disclosure on each child card.
 * Container-level `gap`/`align` sit in a collapsible « Réglages ». Nesting is
 * refused (the library excludes Container; the renderer refuses it too).
 */
const PageEditorLayoutSectionEditor: React.FC<PageEditorLayoutSectionEditorProps> = ({
	editable,
	componentIndex,
	previewComponents,
	updateComponentValue,
	allowImageVariables
}) => {
	// Picker target: a gap index (0..children.length) or "new" (append) or null.
	const [pickerTarget, setPickerTarget] = useState<number | "new" | null>(null);
	// Which (childIndex, breakpoint) width popover is open.
	const [widthPopover, setWidthPopover] = useState<{ index: number; bp: BreakpointKey } | null>(null);
	// Container-level settings (gap / align) disclosure : collapsed by default.
	const [settingsOpen, setSettingsOpen] = useState(false);
	// Per-child « Responsive » disclosures : collapsed by default.
	const [responsiveOpen, setResponsiveOpen] = useState<Record<number, boolean>>({});
	// The child card currently expanded in the nested panel (runtime key).
	const [activeChildKey, setActiveChildKey] = useState<string | null>(null);

	const component = previewComponents[componentIndex];
	const content = ((component as any)?.content ?? {}) as Record<string, any>;
	const children = readChildren(component);
	const gap = GAP_OPTIONS.some((o) => o.value === content.gap) ? (content.gap as string) : "md";
	const align = ALIGN_OPTIONS.some((o) => o.value === content.align) ? (content.align as string) : "start";

	// A child cannot be another Container (renderer refuses nesting) nor a
	// DynamicComponents container (template-only injection point).
	const excludeChildTypes = useCallback(
		(definition: ComponentDefinition) =>
			definition.type === ComponentTypes.LayoutSection ||
			definition.type === ComponentTypes.DynamicComponents,
		[]
	);
	const libraryPicker = useComponentLibraryPicker({ exclude: excludeChildTypes });

	const runtimeImageComponent = useMemo(() => createRuntimeImageComponent(), []);
	const runtimeConfig = useMemo(
		() => ({ disableLinks: true, imageComponent: runtimeImageComponent, assetResolver: resolveStorageUrl }),
		[runtimeImageComponent]
	);

	const setChildren = useCallback(
		(next: LayoutChild[]) => updateComponentValue(componentIndex, ["content", "children"], next),
		[componentIndex, updateComponentValue]
	);

	// --- Nested panel adapters: scope the flat-array callbacks to the children. ---
	const nestedUpdate = useCallback(
		(i: number, path: Path, value: any) =>
			updateComponentValue(componentIndex, ["content", "children", i, ...path], value),
		[componentIndex, updateComponentValue]
	);
	const nestedRemove = useCallback(
		(i: number) => setChildren(children.filter((_, k) => k !== i)),
		[children, setChildren]
	);
	const nestedMove = useCallback(
		(from: number, to: number) => {
			if (to < 0 || to >= children.length) return;
			setChildren(reorderList(children, from, to));
		},
		[children, setChildren]
	);
	const nestedDuplicate = useCallback(
		(i: number) => setChildren(duplicateComponentAt(children, i) as LayoutChild[]),
		[children, setChildren]
	);
	const openPickerAt = useCallback((gapIndex: number) => setPickerTarget(gapIndex), []);
	const openPickerAppend = useCallback(() => setPickerTarget("new"), []);

	const handlePick = useCallback(
		(definition: ComponentDefinition | null) => {
			if (!definition || pickerTarget === null || !editable) return;
			const instance = createComponentInstance(definition);
			const key = `col-${Date.now()}-${children.length}`;
			const insertAt = pickerTarget === "new" ? children.length : pickerTarget;
			const next = [...children];
			next.splice(insertAt, 0, { key, ...instance });
			setChildren(next);
			// Open the child we just inserted: the panel keys cards by type+index, so this is the same
			// key its card will render with.
			setActiveChildKey(getComponentRuntimeKey(definition.type as string, insertAt));
			setPickerTarget(null);
		},
		[children, editable, pickerTarget, setChildren]
	);

	const setSpan = useCallback(
		(index: number, bp: BreakpointKey, value: number | null) => {
			// null = « hériter » (cascades from a larger breakpoint / default 12).
			updateComponentValue(componentIndex, ["content", "children", index, "span", bp], value);
			setWidthPopover(null);
		},
		[componentIndex, updateComponentValue]
	);

	const toggleVisible = useCallback(
		(index: number, bp: BreakpointKey, currentlyVisible: boolean) => {
			updateComponentValue(componentIndex, ["content", "children", index, "visible", bp], !currentlyVisible);
		},
		[componentIndex, updateComponentValue]
	);

	const renderWidthChip = (child: LayoutChild, index: number, bp: BreakpointKey) => {
		const { value, source } = resolveSpanSource(child.span, bp);
		const isExplicit = source === "explicit";
		const open = widthPopover?.index === index && widthPopover?.bp === bp;
		const inheritedLabel =
			source === "default"
				? "hérité (défaut)"
				: source !== "explicit"
					? `hérité du ${BREAKPOINT_LABELS[source as BreakpointKey].toLowerCase()}`
					: "";
		return (
			<div key={bp} className="relative">
				<button
					type="button"
					onClick={() => setWidthPopover(open ? null : { index, bp })}
					disabled={!editable}
					aria-label={`Largeur ${BREAKPOINT_LABELS[bp]}`}
					className={`flex w-full items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
						isExplicit ? "border-[#005CA9] bg-[#005CA9] text-white" : "border-slate-200 bg-white text-slate-500"
					}`}
				>
					<span className="flex items-center gap-1.5">
						<span className="text-[10px] font-medium uppercase tracking-wide opacity-80">{BREAKPOINT_LABELS[bp]}</span>
						<span>{fractionLabel(value)}</span>
					</span>
					{!isExplicit && <span className="text-[10px] font-normal italic opacity-70">{inheritedLabel}</span>}
				</button>
				{open && (
					<div className="absolute left-0 z-30 mt-1 w-44 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
						<div className="grid grid-cols-3 gap-1">
							{SPAN_PRESETS.map((preset) => (
								<button
									key={preset.value}
									type="button"
									onClick={() => setSpan(index, bp, preset.value)}
									className={`rounded-md px-2 py-1 text-xs font-semibold transition ${
										isExplicit && value === preset.value
											? "bg-[#005CA9] text-white"
											: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
									}`}
								>
									{preset.label}
								</button>
							))}
						</div>
						<div className="mt-2 flex items-center gap-1">
							<input
								type="number"
								min={1}
								max={12}
								placeholder="1-12"
								className="w-16 rounded-md border border-slate-200 px-2 py-1 text-xs focus:border-slate-400 focus:outline-none"
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										const n = Number((e.target as HTMLInputElement).value);
										if (Number.isInteger(n) && n >= 1 && n <= 12) setSpan(index, bp, n);
									}
								}}
								aria-label="Largeur personnalisée"
							/>
							<button
								type="button"
								onClick={() => setSpan(index, bp, null)}
								className="flex-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
							>
								Hériter
							</button>
						</div>
					</div>
				)}
			</div>
		);
	};

	// Per-child « Responsive » disclosure, injected into each nested card.
	const renderResponsive = (index: number): React.ReactNode => {
		const child = children[index];
		if (!child) return null;
		const isOpen = !!responsiveOpen[index];
		return (
			<div>
				<button
					type="button"
					onClick={() => setResponsiveOpen((prev) => ({ ...prev, [index]: !prev[index] }))}
					className="flex w-full items-center justify-between rounded-lg px-1 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 transition hover:text-slate-700"
					aria-expanded={isOpen}
				>
					<span className="inline-flex items-center gap-1.5">
						<FiSliders aria-hidden /> Responsive
					</span>
					{isOpen ? <FiChevronUp aria-hidden /> : <FiChevronDown aria-hidden />}
				</button>
				{isOpen && (
					<div className="mt-2 space-y-3">
						<div>
							<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Largeur</p>
							<div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
								{BREAKPOINTS.map((bp) => renderWidthChip(child, index, bp.key))}
							</div>
						</div>
						<div>
							<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Visibilité</p>
							<div className="flex flex-wrap gap-2">
								{BREAKPOINTS.map((bp) => {
									const visible = resolveVisible(child.visible, bp.key);
									const explicit = typeof child.visible?.[bp.key] === "boolean";
									return (
										<button
											key={bp.key}
											type="button"
											onClick={() => toggleVisible(index, bp.key, visible)}
											disabled={!editable}
											aria-label={`Visibilité ${bp.label}`}
											aria-pressed={visible}
											className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
												visible ? "border-slate-200 bg-white text-slate-700" : "border-slate-200 bg-slate-100 text-slate-400"
											} ${explicit ? "" : "opacity-70"}`}
										>
											{visible ? <FiEye aria-hidden /> : <FiEyeOff aria-hidden />}
											{bp.label}
										</button>
									);
								})}
							</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="space-y-2">
			{/* The Container card already wears the dotted border + « CONTAINER » title;
			    here we only expose a light « Réglages » toggle and the nested canvas. */}
			<div className="flex justify-end">
				<button
					type="button"
					onClick={() => setSettingsOpen((v) => !v)}
					aria-expanded={settingsOpen}
					className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
				>
					<FiSliders aria-hidden /> Réglages {settingsOpen ? <FiChevronUp aria-hidden /> : <FiChevronDown aria-hidden />}
				</button>
			</div>

			{settingsOpen && (
				<div className="mb-1 grid gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-2">
					<div className="space-y-1">
						<label className="text-xs font-semibold text-slate-600">Espacement</label>
						<select
							aria-label="Espacement"
							value={gap}
							onChange={(e) => updateComponentValue(componentIndex, ["content", "gap"], e.target.value)}
							disabled={!editable}
							className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
						>
							{GAP_OPTIONS.map((o) => (
								<option key={o.value} value={o.value}>
									{o.label}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-1">
						<label className="text-xs font-semibold text-slate-600">Alignement vertical</label>
						<select
							aria-label="Alignement vertical"
							value={align}
							onChange={(e) => updateComponentValue(componentIndex, ["content", "align"], e.target.value)}
							disabled={!editable}
							className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
						>
							{ALIGN_OPTIONS.map((o) => (
								<option key={o.value} value={o.value}>
									{o.label}
								</option>
							))}
						</select>
					</div>
				</div>
			)}

			{/* The children edited exactly like the page root (nested DnD canvas). */}
			<PageEditorComponentsPanel
				nested
				editable={editable}
				previewComponents={children as All8PageContent}
				activeComponentKey={activeChildKey}
				setActiveComponentKey={setActiveChildKey}
				updateComponentValue={nestedUpdate}
				removeComponent={nestedRemove}
				moveComponent={nestedMove}
				duplicateComponent={nestedDuplicate}
				onInsertAt={openPickerAt}
				onOpenLibrary={openPickerAppend}
				renderCardExtras={renderResponsive}
				allowImageVariables={allowImageVariables}
			/>

			<PageEditorComponentLibraryModal
				show={pickerTarget !== null}
				onClose={() => {
					setPickerTarget(null);
					libraryPicker.reset();
				}}
				picker={libraryPicker}
				onAdd={handlePick}
				runtimeConfig={runtimeConfig}
			/>
		</div>
	);
};

export default PageEditorLayoutSectionEditor;
